import React from "react";

import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";

import { ProductBySlugNonNullable } from "@/common/contracts/pdp.contracts";
import {
  PRODUCT_SEO_COPY,
  formatSellerLocation,
  resolveDeliveryOptionCopy,
  resolveLeafCategoryTitle,
  toProductSeoLocale,
} from "@/common/utils/product-seo.utils";
import { ProductDetailsInfoAttributesEntry } from "@/components/ProductDetail/ProductDetailsInfoAttributesEntry";
import { ProductDetailsPanel } from "@/components/ProductDetail/ProductDetailsPanel";
import { useProductDetailsAttributes } from "@/components/ProductDetail/hooks/useProductDetailsAttributes";

type ProductDetailsInfoAttributesProps = {
  product: ProductBySlugNonNullable;
};

export const ProductDetailsInfoAttributes = ({
  product,
}: ProductDetailsInfoAttributesProps) => {
  const { t } = useTranslation();
  const { locale } = useRouter();
  const seoLocale = toProductSeoLocale(locale);

  // Locale-specific facts that were missing from the specs (locale
  // differentiation, phase 1). The gateway already translates the
  // category; the seller location and delivery option are plain text so a
  // crawler reads them in the page language without any client fetch.
  // Labels fall back to the typed defaults until the Lokalise keys exist.
  const label = (entry: { key: string; defaults: Record<string, string> }) =>
    t(entry.key, { defaultValue: entry.defaults[seoLocale] });

  const leafCategory = resolveLeafCategoryTitle(product);
  const category = leafCategory ? [leafCategory] : [];

  const location = formatSellerLocation(product, seoLocale);
  const sellerLocation = location ? [location] : [];

  const deliveryCopy = resolveDeliveryOptionCopy(product);
  const deliveryOptions = deliveryCopy ? [label(deliveryCopy)] : [];

  const {
    quality,
    color,
    material,
    numberOfItems,
    style,
    artist,
    subject,
    designer,
    brand,
    firstOwner,
    artOrientation,
    artSize,
    height,
    width,
    depth,
    seatHeight,
    usage,
    gender,
    bagModel,
    hardwareColor,
    yearOfPurchase,
  } = useProductDetailsAttributes(product);

  return (
    <>
      <hr />
      <ProductDetailsPanel title={t("pdp:pdp-product-specifications")}>
        <div className="grid grid-cols-[auto_1fr] gap-x-12 gap-y-2">
          {/* Category (localized leaf) */}
          <ProductDetailsInfoAttributesEntry
            label={label(PRODUCT_SEO_COPY.categoryLabel)}
            values={category}
          />
          {/* Condition/Quality */}
          <ProductDetailsInfoAttributesEntry
            label={t("pdp:conditions")}
            values={quality}
          />
          {/* Colors */}
          <ProductDetailsInfoAttributesEntry
            label={t("pdp:pdp-attribute-color")}
            values={color}
          />
          {/* Material */}
          <ProductDetailsInfoAttributesEntry
            label={t("pdp:pdp-attribute-material")}
            values={material}
          />
          {/* Number of items */}
          <ProductDetailsInfoAttributesEntry
            label={t("pdp:pdp-number-of-items")}
            values={numberOfItems}
          />
          {/* Style */}
          <ProductDetailsInfoAttributesEntry
            label={t("pdp:pdp-style")}
            values={style}
          />
          {/* Artist */}
          <ProductDetailsInfoAttributesEntry
            label={t("pdp:pdp-attribute-artist")}
            values={artist}
          />
          {/* Subject */}
          <ProductDetailsInfoAttributesEntry
            label={t("pdp:pdp-attribute-subject")}
            values={subject}
          />
          {/* Designer */}
          <ProductDetailsInfoAttributesEntry
            label={t("pdp:pdp-attribute-brand")}
            values={designer}
          />
          {/* Brand */}
          <ProductDetailsInfoAttributesEntry
            label={t("common:common-brand-title")}
            values={brand}
          />
          {/* Gender (Bags) */}
          <ProductDetailsInfoAttributesEntry
            label={t("common:common-gender")}
            values={gender}
          />
          {/* Model (Bags) */}
          <ProductDetailsInfoAttributesEntry
            label={t("pdp:pdp-attribute-model")}
            values={bagModel}
          />
          {/* Hardware color (Bags) */}
          <ProductDetailsInfoAttributesEntry
            label={t("pdp:pdp-attribute-hardware-color")}
            values={hardwareColor}
          />
          {/* Year of purchase (Bags) */}
          <ProductDetailsInfoAttributesEntry
            label={t("pdp:pdp-attribute-year-of-purchase")}
            values={yearOfPurchase}
          />
          {/* First owner */}
          <ProductDetailsInfoAttributesEntry
            label={t("pdp:pdp-first-owner")}
            values={firstOwner}
          />
          {/* Art ORIENTATION */}
          <ProductDetailsInfoAttributesEntry
            label={t("pdp:pdp-attribute-orientation")}
            values={artOrientation}
          />
          {/* Art size */}
          <ProductDetailsInfoAttributesEntry
            label={t("pdp:pdp-attribute-art-size")}
            values={artSize}
          />
          {/* Height */}
          <ProductDetailsInfoAttributesEntry
            label={t("pdp:pdp-attribute-height")}
            values={height}
          />
          {/* Width */}
          <ProductDetailsInfoAttributesEntry
            label={t("pdp:pdp-attribute-width")}
            values={width}
          />
          {/* Depth */}
          <ProductDetailsInfoAttributesEntry
            label={t("pdp:pdp-attribute-depth")}
            values={depth}
          />
          {/* Seat height */}
          <ProductDetailsInfoAttributesEntry
            label={t("pdp:pdp-attribute-seat-height")}
            values={seatHeight}
          />
          {/* Usage */}
          <ProductDetailsInfoAttributesEntry
            label={t("pdp:use-marks")}
            values={usage}
          />
          {/* Seller location */}
          <ProductDetailsInfoAttributesEntry
            label={label(PRODUCT_SEO_COPY.sellerLocationLabel)}
            values={sellerLocation}
          />
          {/* Delivery options */}
          <ProductDetailsInfoAttributesEntry
            label={label(PRODUCT_SEO_COPY.deliveryOptionsLabel)}
            values={deliveryOptions}
          />
        </div>
      </ProductDetailsPanel>
    </>
  );
};
